import { useEffect, useRef, useState } from "react";
import LanguageSelector from "../LanguageSelector/LanguageSelector";
import { useAuth } from "../../lib/auth";
import { useTranslations } from "../../contexts/Translation";
import { Login } from "../Icons";
import { useOrganizations, useRole } from "../../contexts/UserProvider";
import { useApiData } from "../../contexts/ApiData";
import { useNotifications } from "../../contexts/Notifications";
import { Profile } from "../../types";
import ProfileImage from "../ProfileImage";
import LogoImage from "../LogoImage";
import { getOrgLogoUrl, getProfileImageUrl } from "@/lib/apiHelpers";
import Button from "../Button/Button";
import Link from "../Link/Link";
import styles from "./HamburgerMenu.module.css";
import { homepageUrl } from "../../lib/runtimeConfig";

const HamburgerMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, login, logout } = useAuth();
  const { organizations, currentOrganization } = useOrganizations();
  const t = useTranslations("menu");
  const menuRef = useRef<HTMLDivElement>(null);
  const { get } = useApiData();
  const [profile, setProfile] = useState<Profile | null>(null);
  const role = useRole();
  const { unreadCount } = useNotifications();

  const toggleMenu = () => {
    setIsOpen((current) => !current);
  };

  useEffect(() => {
    const closeDropdown = (e: MouseEvent) => {
      if (!(e.target instanceof Node) || !menuRef.current?.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.body.addEventListener("click", closeDropdown);
    return () => document.body.removeEventListener("click", closeDropdown);
  }, []);

  useEffect(() => {
    const call = async () => {
      const response = await get("/profile");
      setProfile(response);
    };
    call();
  }, [get]);

  const org = organizations?.find((o) => o.id === currentOrganization);

  const NavItem = ({ href, text, onClick = toggleMenu, indented = false, testId }: { href?: string; text: string; onClick?: () => void; indented?: boolean; testId?: string }) => {
    const className = [styles.navItem, indented && styles.navItemIndented].filter(Boolean).join(" ");

    if (href) {
      return (
        <Link className={className} href={href} onClick={onClick} data-testid={testId}>
          {text}
        </Link>
      );
    }

    return (
      <button className={className} onClick={onClick} type='button' data-testid={testId}>
        {text}
      </button>
    );
  };

  const isOwner = () => {
    return role === "owner";
  };

  const image = profile?.image && profile.image.length > 0 ? profile.image : user?.picture || null;

  return (
    <div className={styles.root} ref={menuRef}>
      <div className={styles.header}>
        {org && (
          <div className={styles.organizationBadge}>
            {org && org.logo ? <LogoImage src={getOrgLogoUrl(org.logo)} alt={org.name} maxHeight={50} /> : <span className={styles.organizationName}>{org.name}</span>}
          </div>
        )}
        <button
          className={[styles.toggleButton, isOpen && styles.toggleButtonOpen].filter(Boolean).join(" ")}
          onClick={toggleMenu}
          type='button'
          aria-label={t("openMenu")}
          aria-expanded={isOpen}
        >
          <span className={styles.profileButtonInner}>
            {image ? (
              <span className={styles.profileFrame}>
                <ProfileImage
                  src={profile?.image ? getProfileImageUrl(profile.image) : user?.picture || ""}
                  variant='medium'
                  alt={`${profile?.candidate || user?.name}`}
                />
              </span>
            ) : (
              <span className={styles.profileFrame}>
                <ProfileImage src='' variant='medium' />
              </span>
            )}
            <span aria-live="polite" aria-atomic="true">
              {unreadCount > 0 && (
                <span data-testid='notifications-badge' className={styles.badge} aria-label={t("unreadNotifications", unreadCount)}>
                  {unreadCount}
                </span>
              )}
            </span>
          </span>
        </button>
      </div>
      {isOpen && (
        <div className={styles.menu}>
          <div className={styles.menuHeader}>
            {user ? (
              <>
                <div className={styles.userDetails}>
                  <div className={styles.userImage}>
                    <ProfileImage
                      src={profile?.image ? getProfileImageUrl(profile.image) : user?.picture || ""}
                      variant='large'
                      alt={`${user.name}`}
                    />
                  </div>
                  <div className={styles.candidateName}>{profile?.candidate}</div>
                  <div className={styles.userMeta}>{user.name}</div>
                  <div className={styles.userMeta}>{t(`role_${role}`)}</div>
                </div>
                {org && (
                  <div className={styles.organizationSummary}>
                    {org.logo ? (
                      <div className={styles.organizationLogo}>
                        <LogoImage src={getOrgLogoUrl(org.logo)} alt={org.name} maxHeight={75} />
                      </div>
                    ) : (
                      org.name
                    )}
                  </div>
                )}
              </>
            ) : (
              <Button onClick={() => login()}>
                <Login /> Login
              </Button>
            )}
            <div className={styles.languageSelector}>
              <LanguageSelector label={t("misc.languageSelectorLabel")} name='languageSelector' />
            </div>
          </div>
          <div>
            <NavItem href='/settings?tab=profile' text={t("settings")} />
            <NavItem href='/settings?tab=profile' indented text={t("settings.profile")} />
            {currentOrganization && isOwner() && (
              <>
                <NavItem href='/settings?tab=organization' indented text={t("settings.organization")} />
                <NavItem href='/settings?tab=members' indented text={t("settings.members")} />
              </>
            )}
            <NavItem href='/settings?tab=subscription' indented text={t("subscription.settingsHeading")} />
            <NavItem href='/notifications' testId='notifications-nav-link' text={unreadCount > 0 ? `${t("notifications")} (${unreadCount})` : t("notifications")} />
            <NavItem href='/contact' text={t("contact")} />
            <hr className={styles.separator} />
            <NavItem onClick={() => logout(homepageUrl())} text={t("logout")} />
          </div>
        </div>
      )}
    </div>
  );
};

export default HamburgerMenu;
